<template>
  <div v-if="isEnabled" class="mock-api-inspector" :class="{ 'is-open': isOpen }">
    <button class="mock-api-inspector__toggle" type="button" @click="isOpen = !isOpen">
      <span class="mock-api-inspector__dot" :class="{ 'has-error': hasError }" />
      API
      <span v-if="history.length" class="mock-api-inspector__count">{{ history.length }}</span>
    </button>

    <section v-if="isOpen" class="mock-api-inspector__panel" aria-label="Mock API requests">
      <header class="mock-api-inspector__header">
        <strong>Mock API</strong>
        <div class="mock-api-inspector__actions">
          <button type="button" @click="refresh">更新</button>
          <button type="button" @click="clear">消去</button>
          <button type="button" aria-label="閉じる" @click="isOpen = false">×</button>
        </div>
      </header>

      <p v-if="errorMessage" class="mock-api-inspector__error">{{ errorMessage }}</p>
      <p v-else-if="history.length === 0" class="mock-api-inspector__empty">No requests</p>

      <ol v-else class="mock-api-inspector__list">
        <li v-for="request in visibleHistory" :key="request.at + request.endpoint">
          <div class="mock-api-inspector__line">
            <span class="mock-api-inspector__operation" :data-operation="request.operation">
              {{ request.operation }}
            </span>
            <code>{{ request.endpoint }}</code>
            <span class="mock-api-inspector__status" :class="{ 'has-error': request.status >= 400 }">
              {{ request.status }}
            </span>
          </div>
          <div class="mock-api-inspector__meta">
            {{ request.method }} · {{ request.durationMs }}ms · {{ formatTime(request.at) }}
          </div>
          <details v-if="hasDetail(request)">
            <summary>params / body</summary>
            <pre>{{ detailText(request) }}</pre>
          </details>
        </li>
      </ol>
    </section>
  </div>
</template>

<script setup lang="ts">
interface MockRequestHistoryEntry {
  at: string
  operation: 'READ' | 'CREATE' | 'UPDATE' | 'DELETE' | string
  method: string
  endpoint: string
  status: number
  durationMs: number
  query?: Record<string, unknown>
  body?: unknown
  headers?: Record<string, unknown>
  responsePreview?: string
}

const config = useRuntimeConfig()
const apiBaseUrl = String(config.public.apiBaseUrl ?? '')
const isEnabled = import.meta.client && config.public.mockApiInspector === true
const mockBaseUrl = apiBaseUrl.replace(/\/v2\/?$/, '')

const isOpen = ref(true)
const history = ref<MockRequestHistoryEntry[]>([])
const errorMessage = ref('')
let timer: ReturnType<typeof setInterval> | undefined

const visibleHistory = computed(() => history.value.slice(-20).reverse())
const hasError = computed(() => history.value.some((request) => request.status >= 400))

const refresh = async () => {
  if (!isEnabled) return

  try {
    const response = await fetch(`${mockBaseUrl}/__mock/requests`)
    if (!response.ok) throw new Error(`${response.status} ${response.statusText}`)
    history.value = (await response.json()) as MockRequestHistoryEntry[]
    errorMessage.value = ''
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : 'Failed to load requests'
  }
}

const clear = async () => {
  if (!isEnabled) return

  await fetch(`${mockBaseUrl}/__mock/requests`, { method: 'DELETE' })
  history.value = []
  await refresh()
}

const formatTime = (value: string) =>
  new Intl.DateTimeFormat('ja-JP', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(new Date(value))

const hasValues = (value: unknown) =>
  value !== undefined && typeof value === 'object' && value !== null && Object.keys(value).length > 0

const hasDetail = (request: MockRequestHistoryEntry) =>
  hasValues(request.query) || request.body !== undefined || hasValues(request.headers)

const detailText = (request: MockRequestHistoryEntry) =>
  JSON.stringify(
    {
      query: request.query,
      body: request.body,
      headers: request.headers,
    },
    null,
    2,
  )

onMounted(() => {
  if (!isEnabled) return
  refresh()
  timer = setInterval(refresh, 1500)
})

onBeforeUnmount(() => {
  if (timer) clearInterval(timer)
})
</script>

<style scoped lang="scss">
.mock-api-inspector {
  position: fixed;
  left: 12px;
  bottom: 12px;
  z-index: 10000;
  font-family:
    ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New',
    monospace;
  color: #f8fafc;
}

.mock-api-inspector__toggle {
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 34px;
  padding: 7px 10px;
  border: 1px solid rgba(148, 163, 184, 0.55);
  border-radius: 6px;
  background: rgba(15, 23, 42, 0.94);
  color: #f8fafc;
  font: inherit;
  font-size: 12px;
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.28);
}

.mock-api-inspector__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #22c55e;
}

.mock-api-inspector__dot.has-error {
  background: #ef4444;
}

.mock-api-inspector__count {
  min-width: 18px;
  padding: 1px 5px;
  border-radius: 999px;
  background: #334155;
  text-align: center;
}

.mock-api-inspector__panel {
  width: min(460px, calc(100vw - 24px));
  max-height: min(520px, calc(100vh - 160px));
  margin-bottom: 8px;
  overflow: auto;
  border: 1px solid rgba(148, 163, 184, 0.45);
  border-radius: 8px;
  background: rgba(15, 23, 42, 0.96);
  box-shadow: 0 18px 50px rgba(15, 23, 42, 0.35);
}

.mock-api-inspector.is-open {
  display: flex;
  flex-direction: column-reverse;
}

.mock-api-inspector__header {
  position: sticky;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  background: rgba(15, 23, 42, 0.98);
  border-bottom: 1px solid rgba(148, 163, 184, 0.28);
}

.mock-api-inspector__actions {
  display: flex;
  gap: 6px;

  button {
    padding: 4px 7px;
    border: 1px solid rgba(148, 163, 184, 0.45);
    border-radius: 4px;
    background: #1e293b;
    color: #f8fafc;
    font: inherit;
    font-size: 11px;
    cursor: pointer;
  }
}

.mock-api-inspector__empty,
.mock-api-inspector__error {
  margin: 0;
  padding: 12px;
  color: #cbd5e1;
  font-size: 12px;
}

.mock-api-inspector__error {
  color: #fca5a5;
}

.mock-api-inspector__list {
  display: grid;
  gap: 8px;
  margin: 0;
  padding: 10px;
  list-style: none;
}

.mock-api-inspector__list li {
  padding: 8px;
  border: 1px solid rgba(148, 163, 184, 0.22);
  border-radius: 6px;
  background: rgba(30, 41, 59, 0.74);
}

.mock-api-inspector__line {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 7px;
}

.mock-api-inspector__operation {
  padding: 2px 5px;
  border-radius: 4px;
  background: #2563eb;
  font-size: 10px;
  font-weight: 700;
}

.mock-api-inspector__operation[data-operation='CREATE'] {
  background: #16a34a;
}

.mock-api-inspector__operation[data-operation='UPDATE'] {
  background: #ca8a04;
}

.mock-api-inspector__operation[data-operation='DELETE'] {
  background: #dc2626;
}

.mock-api-inspector__line code {
  overflow: hidden;
  color: #e2e8f0;
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mock-api-inspector__status {
  color: #86efac;
  font-size: 11px;
}

.mock-api-inspector__status.has-error {
  color: #fca5a5;
}

.mock-api-inspector__meta {
  margin-top: 4px;
  color: #94a3b8;
  font-size: 10px;
}

.mock-api-inspector details {
  margin-top: 6px;
  color: #cbd5e1;
  font-size: 10px;
}

.mock-api-inspector summary {
  cursor: pointer;
}

.mock-api-inspector pre {
  max-height: 180px;
  margin: 6px 0 0;
  overflow: auto;
  color: #e2e8f0;
  white-space: pre-wrap;
}

@media (max-width: 599px) {
  .mock-api-inspector {
    bottom: 104px;
  }
}
</style>
